// C:\react\src\App.jsx - FINAL VERSION WITH ALL ROUTING FIXED

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom'; 
import Sidebar from './components/Sidebar';
import BookDetail from './components/BookDetail';
import BooksList from './components/BooksList';
import About from './components/About';
import Contact from './components/Contact';
import Guestbook from './components/Guestbook';
import GuestbookAdmin from './components/GuestbookAdmin';
import LiveFuneral from './components/LiveFuneral';
import BishopTabet from './components/BishopTabet';
import FamilyEulogy from './components/FamilyEulogy';
import MemorialBooklet from './components/MemorialBooklet';
import BuryMeInAychieh from './components/BuryMeInAychieh';
import AbounaLifeLegacy from './components/AbounaLifeLegacy';
import GalleryPhotos, { PublicPhotos, PrivatePhotos } from './components/GalleryPhotos';
import GalleryVideos, { PublicVideos, PrivateVideos } from './components/GalleryVideos';
// 🔥 CRITICAL FIX 1: Import the FacebookPosts component
import FacebookPosts from './components/FacebookPosts'; 
import './App.css';
import './styles/MemorialTheme.css';
import './styles/TypographySidebar.css';

// This is the component that is rendered inside the main <BrowserRouter> in main.jsx
function App() {
   const location = useLocation();
   
   // --- app state ---
   const [selectedBook, setSelectedBook] = useState(null);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState(null);
   const [books, setBooks] = useState([]);
   const [posts, setPosts] = useState([]);

   // --- responsive + drawer state ---
   const [isMobile, setIsMobile] = useState(() => {
      if (typeof window === 'undefined') return false;
      return window.innerWidth < 1024;
   });
   
   const [sidebarOpen, setSidebarOpen] = useState(false);
   const [isOnline, setIsOnline] = useState(navigator.onLine);

   // Derive selectedItem from current route
   const selectedItem = useMemo(() => {
      const path = location.pathname;
      if (path === '/') return 'home';
      if (path.startsWith('/gallery')) return 'gallery';
      if (path.startsWith('/books')) return 'books';
      if (path === '/facebook') return 'facebook';
      if (path === '/obituary' || path === '/about') return 'obituary';
      if (path === '/contact') return 'contact';
      if (path === '/live') return 'live';
     if (path === '/bishop-tabet') return 'bishop-tabet';
     if (path === '/family-eulogy') return 'family-eulogy';
     if (path === '/memorial-booklet') return 'memorial-booklet';
     if (path === '/bury-me-in-aychieh') return 'bury-me-in-aychieh';
     if (path === '/abouna-life-and-legacy') return 'abouna-life-and-legacy';
      if (path.startsWith('/guestbook')) return 'guestbook';
      return 'home';
   }, [location.pathname]);

   // Handle book selection
   const handleBookSelect = useCallback((book) => {
      setSelectedBook(book);
      if (isMobile) {
         setSidebarOpen(false);
      }
   }, [isMobile]);

   // Handle navigation for mobile
   const handleNavigation = useCallback(() => {
      if (isMobile) {
         setSidebarOpen(false);
      }
      setError(null);
   }, [isMobile]);

   // Smooth UX: scroll to top on section change
   useEffect(() => {
      try {
         window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
         window.scrollTo(0, 0);
      }
   }, [location.pathname, selectedBook]);

   // Keep an eye on viewport to switch mobile/desktop behavior
   useEffect(() => {
      const onResize = () => {
         const mobile = window.innerWidth < 1024;
         setIsMobile(mobile);
         if (!mobile) setSidebarOpen(false);
      };
      
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
   }, []);

   // Online/offline detection
   useEffect(() => {
      const handleOnline = () => setIsOnline(true);
      const handleOffline = () => setIsOnline(false);
      
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
      
      return () => {
         window.removeEventListener('online', handleOnline);
         window.removeEventListener('offline', handleOffline);
      };
   }, []);

   // Inline styles for the mobile drawer/overlay
   const styles = useMemo(() => {
      const overlayBase = {
         position: 'fixed',
         inset: 0,
         background: 'rgba(0,0,0,0.35)',
         opacity: 0,
         pointerEvents: 'none',
         transition: 'opacity 160ms ease',
         zIndex: 999,
      };
      
      const overlayShow = sidebarOpen ? { 
         opacity: 1, 
         pointerEvents: 'auto' 
      } : {};
      
      const asideBase = {
         // default: let desktop use your existing CSS layout
      };
      
      const drawerBase = {
         position: 'fixed',
         top: 0,
         bottom: 0,
         left: 0,
         width: 'min(86vw, 320px)',
         transform: 'translateX(-100%)',
         transition: 'transform 200ms ease-out',
         zIndex: 1000,
         background: '#1e1e22',
         color: '#fff',
         overflowY: 'auto',
         borderRight: '1px solid rgba(255,255,255,0.08)',
         padding: '12px 10px 16px',
      };
      
      const drawerOpen = { transform: 'translateX(0)' };
      
      const headerBase = {
         position: 'sticky',
         top: 0,
         display: 'flex',
         alignItems: 'center',
         gap: 12,
         padding: '12px 14px',
         background: '#ffffffcc',
         backdropFilter: 'saturate(1.1) blur(8px)',
         WebkitBackdropFilter: 'saturate(1.1) blur(8px)',
         borderBottom: '1px solid #eee',
         zIndex: 50,
      };
      
      const buttonBase = {
         appearance: 'none',
         border: '1px solid #ddd',
         background: '#fff',
         fontSize: 20,
         borderRadius: 10,
         padding: '8px 10px',
         cursor: 'pointer',
      };
      
      const closeBtn = {
         ...buttonBase,
         position: 'absolute',
         top: 8,
         right: 8,
         zIndex: 1001,
         background: 'transparent',
         color: '#fff',
         border: '1px solid rgba(255,255,255,0.25)',
      };

      return {
         overlay: isMobile ? { ...overlayBase, ...overlayShow } : { display: 'none' },
         aside: isMobile ? { ...drawerBase, ...(sidebarOpen ? drawerOpen : {}) } : asideBase,
         mobileHeader: isMobile ? headerBase : { display: 'none' },
         hamburger: buttonBase,
         brand: { fontWeight: 600, fontSize: 16 },
         closeBtn: isMobile ? closeBtn : { display: 'none' },
         main: isMobile ? { padding: 16 } : {},
         offlineBanner: {
            background: '#ff6b6b',
            color: 'white',
            textAlign: 'center',
            padding: '8px',
            fontSize: '14px',
            position: 'sticky',
            top: 0,
            zIndex: 100,
         },
      };
   }, [isMobile, sidebarOpen]);

   // Show loading spinner
   if (loading) {
      return (
         <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            backgroundColor: '#1a1a1a',
            color: '#ffcc00',
            flexDirection: 'column',
            gap: '20px'
         }}>
            <div style={{
               width: '50px',
               height: '50px',
               border: '4px solid rgba(255, 204, 0, 0.3)',
               borderTop: '4px solid #ffcc00',
               borderRadius: '50%',
               animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ fontSize: '1.2rem', margin: 0 }}>Loading...</p>
            
            <style>
               {`
                  @keyframes spin {
                     0% { transform: rotate(0deg); }
                     100% { transform: rotate(360deg); }
                  }
               `}
            </style>
         </div>
      );
   }

   return (
      <div className="app-layout">
         {/* Offline indicator */}
         {!isOnline && (
            <div style={styles.offlineBanner}>
               You are currently offline. Some features may be unavailable.
            </div>
         )}

         {/* Mobile header */}
         <header className="mobile-memorial-header" style={styles.mobileHeader} aria-label="Mobile header">
             <button
               className="mobile-menu-button"
               style={styles.hamburger}
               aria-label="Open menu"
               onClick={() => setSidebarOpen(true)}
            >
               ☰
            </button>
             <div className="mobile-memorial-brand" style={styles.brand}>
               <span aria-hidden="true">✝</span>
               Father Paul Oneid
             </div>
         </header>

         {/* Drawer overlay (mobile only) */}
         <div
            role="button"
            aria-label="Close menu overlay"
            tabIndex={sidebarOpen ? 0 : -1}
            onClick={() => setSidebarOpen(false)}
            onKeyDown={(e) => (e.key === 'Escape' ? setSidebarOpen(false) : null)}
            style={styles.overlay}
         />

         {/* Sidebar: docked on desktop, drawer on mobile */}
         <aside
            className="sidebar"
            style={styles.aside}
            aria-hidden={isMobile ? String(!sidebarOpen) : 'false'}
            aria-label="Sidebar navigation"
         >
            {isMobile && (
               <button
                  aria-label="Close menu"
                  onClick={() => setSidebarOpen(false)}
                  style={styles.closeBtn}
               >
                  ✕
               </button>
            )}

            {/* Sidebar component */}
            <Sidebar onItemClick={handleNavigation} />
         </aside>

         {/* Main content */}
         <div className="main-area" style={styles.main}>
            {/* Error display */}
            {error && (
               <div className="error-banner">
                  {error}
                  <button onClick={() => setError(null)}>×</button>
               </div>
            )}

            <header className="site-sacred-banner">
               <img
                  src="/theme/father-paul-banner.png"
                  alt="Father Paul Oneid, Maronite Catholic Priest, framed by grape vines, wheat, a cross, chalice, and bread"
                  loading="eager"
               />
            </header>

            <main className="sacred-page-frame">
               <div className="sacred-page-frame__content">
                  {/* Routes */}
                  <Routes>
               <Route path="/" element={
                  <div className="home-page">
                     <figure className="home-feature-portrait">
                       <img
                          src="/theme/father-paul-homepage-picture.jpeg"
                          alt="Father Paul Oneid in youth and later life before the cedars of Lebanon; Arabic text honors one hundred years of grace and faith"
                          loading="eager"
                       />
                     </figure>
                     <figure className="home-circular-portrait">
                       <img
                         src="/theme/father-paul-homepage-picture-2.jpeg"
                         alt="Father Paul Oneid in a circular gold memorial frame before the cedars of Lebanon"
                         loading="eager"
                       />
                     </figure>
                     <div className="home-pane">
                     <div className="home-portrait-frame">
                       <img
                          src="/media/photos/father-winter.png"
                          alt="Father Paul Oneid standing in the snowy hills of Lebanon"
                          className="full-picture"
                          loading="eager"
                       />
                     </div>
                     <div className="home-content">
                        <div className="home-text-frame">
                           <div className="home-cross" aria-hidden="true">✝</div>
                           <p className="home-kicker">Faith · Family · Service</p>
                           <h1>Reverend Father<br />Paul Oneid</h1>
                           <div className="home-rule" aria-hidden="true"><span>◆</span></div>
                           <p className="home-intro">
                              A Maronite Catholic priest, author, educator, church builder, and devoted
                              family patriarch whose life was defined by unwavering faith, spiritual
                              leadership, and a lifelong commitment to serving God, Church, family, and community.
                           </p>
                           <p className="home-note">
                              Explore his books, photographs, videos, and writings preserved here
                              for family, parishioners, and future generations.
                           </p>
                        </div>
                     </div>
                     </div>
                  </div>
               } />
               
               <Route path="/books" element={<BooksList />} />
               <Route path="/books/:id" element={<BookDetail />} />
               
               {/* GALLERY ROUTES */}
              <Route path="/gallery" element={<GalleryPhotos />} />
              <Route path="/gallery/photos" element={<GalleryPhotos />} />
              <Route path="/gallery/photos/public" element={<PublicPhotos />} />
              <Route path="/gallery/photos/private" element={<PrivatePhotos />} />
               <Route path="/gallery/videos" element={<GalleryVideos />} />
               <Route path="/gallery/videos/public" element={<PublicVideos />} />
               <Route path="/gallery/videos/private" element={<PrivateVideos />} />
               
                {/* 🔥 CRITICAL FIX 2: Add the Facebook Posts route here! */}
                <Route path="/facebook" element={<FacebookPosts />} /> 
                
               <Route path="/obituary" element={<About />} />
               <Route path="/about" element={<Navigate to="/obituary" replace />} />
              <Route path="/contact" element={<Contact />} />
             <Route path="/live" element={<LiveFuneral />} />
             <Route path="/abouna-life-and-legacy" element={<AbounaLifeLegacy />} />
             <Route path="/bishop-tabet" element={<BishopTabet />} />
             <Route path="/family-eulogy" element={<FamilyEulogy />} />
              <Route path="/memorial-booklet" element={<MemorialBooklet />} />
              <Route path="/bury-me-in-aychieh" element={<BuryMeInAychieh />} />
              <Route path="/guestbook" element={<Guestbook />} />
               <Route path="/guestbook/admin" element={<GuestbookAdmin />} />
               
               {/* Redirects for common variations */}
               <Route path="/home" element={<Navigate to="/" replace />} />
               
               {/* Fallback to home */}
               <Route path="*" element={
                  <div className="not-found">
                     <h2>Page Not Found</h2>
                     <p>The page you're looking for doesn't exist.</p>
                     <button onClick={() => window.history.back()}>
                        Go Back
                     </button>
                  </div>
               } />
                  </Routes>
               </div>
            </main>

            <footer className="memorial-ribbon" aria-label="Father Paul Oneid memorial artwork">
               <img
                  src="/theme/father-paul-obituary-footer.png"
                  alt="Three portraits of Father Paul Oneid beside three churches"
                  loading="lazy"
               />
            </footer>
         </div>
      </div>
   );
}

export default App;
